// The relative imports below carry the .js extension the rest of the portal leaves off:
// the conformance harness compiles this file with tsc and runs it on node, which needs it.
import type { FontRef, ID, Story, Thread } from '@proofroom/schema';
import { faceEmphasis, type StyleLookup } from './attrs.js';
import type { PMMark, PMNode } from './pmJson.js';

export interface StoryToDocContext extends Pick<StyleLookup, 'paraStyles' | 'charStyles'> {
  /** the draft's *available* faces, the same list materialise resolves against */
  fonts: Record<ID, FontRef>;
  /** threads anchored in this story, so their ranges become `comment` marks */
  threads?: Thread[];
}

interface Boundary { at: number; open: PMMark[]; close: string[] }

/**
 * Story to ProseMirror JSON, the inverse of materialise (runs become face / bold / italic /
 * charStyle marks).
 *
 * The `face` mark carries the run's resolved fontId verbatim, because bold and italic are one
 * directional: they can say "bolder than the paragraph" and have no way to say "lighter", so a
 * light run inside a bold paragraph (the contents page, the bullets, the Body copy sub-items) had
 * nothing to travel in and came back bold. The emphasis marks are still emitted alongside it, so
 * the toolbar reads the same as before; materialise decides between them by recomputing the
 * emphasis this face implies (see runAttrsFor).
 */
export function storyToDoc(story: Story, ctx: StoryToDocContext): PMNode {
  const content: PMNode[] = story.paragraphs.map((p) => {
    const baseFont = ctx.paraStyles[p.styleId]?.attrs.fontId ?? '';
    const cuts = new Set<number>([0, p.text.length]);
    for (const r of p.runs) { cuts.add(r.start); cuts.add(r.end); }
    const threads = (ctx.threads ?? []).filter((t) => t.anchor.storyId === story.id && t.anchor.paraId === p.id && t.anchor.start !== undefined && t.anchor.end !== undefined);
    for (const t of threads) { cuts.add(clamp(t.anchor.start!, p.text.length)); cuts.add(clamp(t.anchor.end!, p.text.length)); }
    const points = [...cuts].sort((a, b) => a - b);
    const children: PMNode[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      const s = points[i], e = points[i + 1];
      if (e <= s) continue;
      const marks: PMMark[] = [];
      const run = p.runs.find((r) => r.start <= s && r.end >= e);
      if (run?.charStyleId) marks.push({ type: 'charStyle', attrs: { id: run.charStyleId } });
      const fontId = run?.overrides?.fontId;
      // a face the draft does not have is not marked, so materialise cannot hand it back as a run
      // override; without this a font the draft declares missing would survive the round trip
      if (fontId && ctx.fonts[fontId]) {
        const em = faceEmphasis(ctx.fonts, run?.charStyleId ? (ctx.charStyles[run.charStyleId]?.attrs.fontId ?? baseFont) : baseFont, fontId);
        if (em.bold) marks.push({ type: 'bold' });
        if (em.italic) marks.push({ type: 'italic' });
        marks.push({ type: 'face', attrs: { fontId } });
      }
      for (const t of threads) {
        if (clamp(t.anchor.start!, p.text.length) <= s && clamp(t.anchor.end!, p.text.length) >= e) marks.push({ type: 'comment', attrs: { threadId: t.id } });
      }
      const node: PMNode = { type: 'text', text: p.text.slice(s, e) };
      if (marks.length) node.marks = marks;
      children.push(node);
    }
    const para: PMNode = { type: 'paragraph', attrs: { paraId: p.id, styleId: p.styleId } };
    if (children.length) para.content = children;
    return para;
  });
  return { type: 'doc', content };
}

function clamp(n: number, max: number): number { return Math.max(0, Math.min(max, n)); }

export type { Boundary };
